import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { PERMISSIONS, latToDms, lonToDms } from '@jupi/shared'
import { ConfirmDialog, IconButton, RemoteContent, Title, useAuth, useSnackbar } from '@jupi/ui'
import {
  useCountries,
  useDeleteWaypoint,
  useShareWaypoint,
  useWaypoints,
  type Country,
  type PlanWaypoint,
} from '../../api/plan'
import AerodromeDialog from './AerodromeDialog'
import DataSourcesNote from './DataSourcesNote'
import { useNearMe } from './useNearMe'
import WaypointDialog from './WaypointDialog'

// Every waypoint the member can plan with: the club's reference points,
// their own, and shared ones. A phone gets one card per point and a
// "nearest" button that sorts by distance from where they stand; a desktop
// gets webplan's table.
export default function WaypointsPage() {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const wide = useMediaQuery(theme.breakpoints.up('md'))
  const { showSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [country, setCountry] = useState('fi')
  const [own, setOwn] = useState(false)
  const nearMe = useNearMe(() => showSnackbar(t('plan.waypoints.locationDenied'), { severity: 'warning' }))
  // null: closed; 'new'; or the waypoint being edited.
  const [editing, setEditing] = useState<PlanWaypoint | 'new' | null>(null)
  const [deleting, setDeleting] = useState<PlanWaypoint | null>(null)
  const [aerodrome, setAerodrome] = useState<string | null>(null)
  const deleteWaypoint = useDeleteWaypoint()

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const { data: countries = [] } = useCountries()
  const { data: waypoints = [], isLoading, error } = useWaypoints({
    q: q || undefined,
    // Nearest-first searches every country: the nearest point may be over a border.
    country: nearMe.position || country === '' ? undefined : country,
    own,
    near: nearMe.position ?? undefined,
  })

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title label={t('nav.planWaypoints')} />
        <Button variant="contained" startIcon={<Icon icon="mdi:plus" />} onClick={() => setEditing('new')}>
          {t('plan.waypoints.new')}
        </Button>
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('plan.waypoints.search')}
          slotProps={{
            htmlInput: { autoCapitalize: 'characters', enterKeyHint: 'search' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mdi:magnify" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant={nearMe.position ? 'contained' : 'outlined'}
            startIcon={<Icon icon="mdi:satellite-variant" />}
            onClick={nearMe.toggle}
            disabled={nearMe.locating}
          >
            {nearMe.position ? t('plan.waypoints.nearMeOff') : t('plan.waypoints.nearMe')}
          </Button>
          {!nearMe.position ? (
            <TextField
              select
              size="small"
              label={t('plan.waypoints.country')}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">{t('plan.waypoints.allCountries')}</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {countryName(c, i18n.language)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <FormControlLabel
            control={<Switch checked={own} onChange={(e) => setOwn(e.target.checked)} />}
            label={t('plan.waypoints.ownOnly')}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('plan.waypoints.count', { count: waypoints.length })}
        </Typography>
      </Stack>

      <RemoteContent isLoading={isLoading} error={error}>
        {waypoints.length === 0 ? (
          <Typography color="text.secondary">{t('plan.waypoints.none')}</Typography>
        ) : wide ? (
          <WaypointTable waypoints={waypoints} onEdit={setEditing} onDelete={setDeleting} onDetails={setAerodrome} />
        ) : (
          <Stack spacing={1}>
            {waypoints.map((w) => (
              <WaypointCard key={w.code} waypoint={w} onEdit={setEditing} onDelete={setDeleting} onDetails={setAerodrome} />
            ))}
          </Stack>
        )}
      </RemoteContent>

      <Box sx={{ mt: 1 }}>
        <DataSourcesNote />
      </Box>
      <AerodromeDialog code={aerodrome} onClose={() => setAerodrome(null)} />
      <WaypointDialog open={editing !== null} waypoint={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={deleting !== null}
        title={t('plan.waypoints.delete')}
        message={t('plan.waypoints.deleteConfirm', { code: deleting?.code ?? '' })}
        confirmLabel={t('plan.waypoints.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          if (deleting) {
            deleteWaypoint.mutate(deleting.code, {
              onError: () => showSnackbar(t('common.error'), { severity: 'error' }),
            })
          }
          setDeleting(null)
        }}
        onClose={() => setDeleting(null)}
      />
    </Box>
  )
}

interface RowActions {
  onEdit: (waypoint: PlanWaypoint) => void
  onDelete: (waypoint: PlanWaypoint) => void
  // An aerodrome's runways and frequencies.
  onDetails: (code: string) => void
}

// What this member may do with a point (services/planAccess.ts on the
// server): edit and delete their own; fpl.admin also the club's and
// shared ones, and decides what is shared.
function Actions({ waypoint, onEdit, onDelete, onDetails }: { waypoint: PlanWaypoint } & RowActions) {
  const { t } = useTranslation()
  const { user, can } = useAuth()
  const share = useShareWaypoint()
  const admin = can(PERMISSIONS.fplAdmin)
  const own = waypoint.ownerUserId !== null && waypoint.ownerUserId === user?.id
  const details =
    waypoint.waypointType === 'ad' ? (
      <IconButton aria-label={t('plan.aerodrome.open')} onClick={() => onDetails(waypoint.code)}>
        <Icon icon="mdi:information-outline" />
      </IconButton>
    ) : null
  if (!own && !admin) return details
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      {details}
      {admin && waypoint.ownerUserId !== null ? (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={waypoint.isShared}
              onChange={(e) => share.mutate({ code: waypoint.code, isShared: e.target.checked })}
            />
          }
          label={t('plan.waypoints.shareWithAll')}
          slotProps={{ typography: { variant: 'body2' } }}
        />
      ) : null}
      <IconButton aria-label={t('plan.waypoints.edit')} onClick={() => onEdit(waypoint)}>
        <Icon icon="mdi:pencil-outline" />
      </IconButton>
      <IconButton aria-label={t('plan.waypoints.delete')} onClick={() => onDelete(waypoint)}>
        <Icon icon="mdi:delete-outline" />
      </IconButton>
    </Stack>
  )
}

export function countryName(country: Country, language: string) {
  if (language.startsWith('sv')) return country.nameSv
  if (language.startsWith('en')) return country.nameEn
  return country.nameFi
}

function OwnerChip({ waypoint }: { waypoint: PlanWaypoint }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  if (waypoint.ownerUserId === null) return null
  if (waypoint.ownerUserId === user?.id) return <Chip size="small" color="primary" variant="outlined" label={t('plan.waypoints.own')} />
  return <Chip size="small" variant="outlined" label={t('plan.waypoints.shared')} />
}

function Distance({ nm }: { nm: number | null }) {
  if (nm === null) return null
  return (
    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {nm < 10 ? nm.toFixed(1) : Math.round(nm)} NM
    </Typography>
  )
}

function WaypointCard({ waypoint, ...actions }: { waypoint: PlanWaypoint } & RowActions) {
  const { t } = useTranslation()
  const frequencies = (['com', 'gnd', 'atis'] as const).filter((key) => waypoint[key])
  return (
    <Card>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
            {waypoint.code}
          </Typography>
          <OwnerChip waypoint={waypoint} />
          <Box sx={{ flex: 1 }} />
          <Distance nm={waypoint.distanceNm} />
        </Stack>
        {waypoint.name ? (
          <Typography variant="body2" color="text.secondary">
            {waypoint.name}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {latToDms(waypoint.lat)} {lonToDms(waypoint.lon)}
          {waypoint.variation !== null ? ` · ${t('plan.waypoints.variation')} ${waypoint.variation}°` : ''}
        </Typography>
        {frequencies.length > 0 ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {frequencies.map((key) => `${t(`plan.waypoints.${key}`)} ${waypoint[key]}`).join(' · ')}
          </Typography>
        ) : null}
        {waypoint.link ? <ExternalLink href={waypoint.link} /> : null}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
          <Actions waypoint={waypoint} {...actions} />
        </Box>
      </CardContent>
    </Card>
  )
}

function WaypointTable({ waypoints, ...actions }: { waypoints: PlanWaypoint[] } & RowActions) {
  const { t } = useTranslation()
  const near = waypoints.some((w) => w.distanceNm !== null)
  return (
    <TableContainer component={Card}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('plan.waypoints.code')}</TableCell>
            <TableCell>{t('plan.waypoints.name')}</TableCell>
            {near ? <TableCell align="right">NM</TableCell> : null}
            <TableCell>{t('plan.waypoints.position')}</TableCell>
            <TableCell>{t('plan.waypoints.variation')}</TableCell>
            <TableCell>{t('plan.waypoints.com')}</TableCell>
            <TableCell>{t('plan.waypoints.gnd')}</TableCell>
            <TableCell>{t('plan.waypoints.atis')}</TableCell>
            <TableCell>{t('plan.waypoints.area')}</TableCell>
            <TableCell>{t('plan.waypoints.vor')}</TableCell>
            <TableCell>{t('plan.waypoints.link')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {waypoints.map((w) => (
            <TableRow key={w.code} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{w.code}</span>
                  <OwnerChip waypoint={w} />
                </Stack>
              </TableCell>
              <TableCell>{w.name}</TableCell>
              {near ? (
                <TableCell align="right">
                  <Distance nm={w.distanceNm} />
                </TableCell>
              ) : null}
              <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                <div>{latToDms(w.lat)}</div>
                <div>{lonToDms(w.lon)}</div>
              </TableCell>
              <TableCell>{w.variation ?? ''}</TableCell>
              <TableCell>{w.com}</TableCell>
              <TableCell>{w.gnd}</TableCell>
              <TableCell>{w.atis}</TableCell>
              <TableCell>{w.area}</TableCell>
              <TableCell>{w.vor}</TableCell>
              <TableCell>{w.link ? <ExternalLink href={w.link} /> : null}</TableCell>
              <TableCell align="right">
                <Actions waypoint={w} {...actions} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// A member-entered link: only ever http(s), never a javascript: URL.
export function ExternalLink({ href }: { href: string }) {
  const { t } = useTranslation()
  if (!/^https?:\/\//i.test(href)) return null
  return (
    <Link href={href} target="_blank" rel="noreferrer" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {t('plan.map.moreInfo')}
      <Icon icon="mdi:open-in-new" />
    </Link>
  )
}
